// Свой экземпляр репортера, а НЕ глобальный `authLib.reportToSentry`, которым
// пользуется PayHelper.coffee, — и это не забытое правило 28, а единственный
// работающий вариант. Сайтовый бандл едет как `<script type="module">`, где
// объявление `var authLib` остаётся в области модуля и на `window` не попадает
// (глобалом оно становится только в legacy-ветке `nomodule`). react-ui.js —
// отдельный классический скрипт, то есть на современном браузере он этого
// глобала не видит вовсе, и отчёт молча не уходил бы — ровно тот отказ, что
// чинится ниже. Цена измерена: +1115 байт минифицированных, +456 в gzip
// (2389 → 2845 B). Импорт точечный, а не из бочки `@/util`, чтобы не затащить
// сюда ajax/metrika: файл грузится на каждой странице открытки (правило 25).
import { reportToSentry } from '@/util/sentry-report';
import type {
  IComponentHandle,
  IComponentModule,
  TComponentName,
  IShowOptions,
} from './types';

// Inline loader styles (avoids css-loader overhead in main bundle)
const LOADER_STYLES = `
.reactui-portal{position:fixed;z-index:10000}
.reactui-loader{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%)}
.reactui-spinner{width:40px;height:40px;border:3px solid #f3f3f3;border-top:3px solid #007bff;border-radius:50%;animation:reactui-spin .8s linear infinite}
@keyframes reactui-spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
`;

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.textContent = LOADER_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
}

// Component loaders - these trigger dynamic imports
const components: Record<TComponentName, () => Promise<IComponentModule>> = {
  'payment-drawer': () => import('./components/payment-drawer'),
  'greeting-text-generator': () => import('./components/greeting-text-generator'),
};

// Cache for loaded modules
const cache = new Map<TComponentName, IComponentModule>();

// Cache for renderer
let rendererPromise: Promise<typeof import('./renderer')> | null = null;

// Кэшируем только УСПЕШНУЮ загрузку рантайма. Отклонённый промис, оставшийся в
// кэше, отравляет все последующие показы: предзагрузка (preload) идёт сама,
// через 7 с после загрузки страницы, и один её сбой навсегда ломал кассу на
// этой вкладке — клик по «оплатить» отклонялся мгновенно, ничего не запрашивая.
function loadRenderer(): Promise<typeof import('./renderer')> {
  if (!rendererPromise) {
    rendererPromise = import('./renderer').catch((e) => {
      rendererPromise = null;
      throw e;
    });
  }

  return rendererPromise;
}

// Track mounted components
const mounted = new Map<TComponentName, IComponentHandle>();

export async function show(
  name: TComponentName,
  options: IShowOptions = {}
): Promise<IComponentHandle> {
  const componentLoader = components[name];
  if (!componentLoader) throw new Error(`Unknown component: ${name}`);

  // Inject loader styles on first call
  injectStyles();

  // Create portal container immediately
  const container = document.createElement('div');
  container.className = 'reactui-portal';
  document.body.appendChild(container);

  // Show CSS-only loader while React chunk loads
  const loader = document.createElement('div');
  loader.className = 'reactui-loader';
  loader.innerHTML = '<div class="reactui-spinner"></div>';
  container.appendChild(loader);

  const cleanup = (): void => {
    container.remove();
    mounted.delete(name);
  };

  try {
    // Load component and renderer in parallel
    if (!cache.has(name)) {
      cache.set(name, await componentLoader());
    }

    const [module, renderer] = await Promise.all([
      Promise.resolve(cache.get(name)!),
      loadRenderer(),
    ]);

    // Remove loader and mount React component
    loader.remove();

    const unmount = renderer.render(container, module, options, cleanup);

    const handle: IComponentHandle = { unmount };
    mounted.set(name, handle);
    return handle;
  } catch (e) {
    // Чанк не загрузился — спиннер иначе крутится поверх страницы до самой
    // перезагрузки, и человек читает это как «оплата открывается», хотя касса
    // не появится уже никогда. Убираем портал и отдаём причину наверх: это
    // единственное место, где известно, ЧТО именно не доехало.
    //
    // Именно container.remove(), а НЕ cleanup(): реестр `mounted` общий, и
    // запись под этим именем может принадлежать живому компоненту от прошлого
    // удачного show() — вычистив её, неудачная попытка превратила бы
    // hide() для него в молчаливый no-op. Своей записи у неё ещё нет.
    container.remove();
    throw e;
  }
}

export function hide(name: TComponentName): void {
  mounted.get(name)?.unmount();
}

export async function preload(name: TComponentName): Promise<void> {
  const componentLoader = components[name];
  if (!componentLoader) return;

  if (!cache.has(name)) {
    cache.set(name, await componentLoader());
  }

  await loadRenderer();
}

// Auto-preload from data attributes on the <script> tag:
//   data-preload="component-name"
//   data-preload-delay="7000"        (ms after page preloader finishes)
//   data-preload-trigger="click:.play-control"  (event:selector)
function setupAutoPreload(): void {
  const scriptTag = document.querySelector('script[src*="react-ui"][data-preload]');
  if (!scriptTag) return;

  const name = scriptTag.getAttribute('data-preload') as TComponentName;
  if (!components[name]) return;

  const delay = parseInt(scriptTag.getAttribute('data-preload-delay') || '7000', 10);
  const trigger = scriptTag.getAttribute('data-preload-trigger');

  let done = false;
  function doPreload(): void {
    if (done) return;
    done = true;
    // Отказ предзагрузки ничего не ломает — состояние загрузчика после сбоя
    // чистое, и клик запросит чанк заново, — но глушить его молча нельзя: она
    // срабатывает через 7 с после загрузки страницы у ВСЕХ, то есть первой
    // увидит разъехавшийся после деплоя хеш чанка, задолго до того, как
    // кто-нибудь нажмёт «оплатить». Без этого отчёта состояние «касса не
    // откроется ни у кого» невидимо до первого клика. Warning, а не error:
    // покупка ещё не заблокирована.
    preload(name).catch((e) => {
      reportToSentry({
        section: 'react_ui_preload',
        level: 'warning',
        error: e,
        tags: {
          component: name,
          preload_error: e instanceof Error ? e.name : typeof e,
        },
      });
    });
  }

  // Wait for page preloader to finish, then start delay
  const MAX_PRELOADER_POLLS = 150; // 30s max wait
  (function waitForPreloader(attempt = 0): void {
    if (window.PP?.finished || attempt >= MAX_PRELOADER_POLLS) {
      setTimeout(doPreload, delay);
    } else {
      setTimeout(() => waitForPreloader(attempt + 1), 200);
    }
  })();

  // Immediate preload on trigger event
  if (trigger) {
    const [event, selector] = trigger.split(':');
    document.addEventListener(event, function handler(e: Event) {
      if (!selector || (e.target as Element).closest(selector)) {
        doPreload();
        document.removeEventListener(event, handler);
      }
    });
  }
}

setupAutoPreload();
